import React from 'react';

const TreatmentOptions: React.FC = () => {
  const treatments = [
    {
      title: 'Testosterone Optimization',
      description: 'Restore optimal testosterone levels for enhanced energy, muscle mass, and vitality.',
      benefits: ['Increased energy and stamina', 'Improved muscle mass and strength', 'Enhanced libido and sexual function', 'Better mood and mental clarity'],
      methods: ['Testosterone Cypionate Injections', 'Topical Testosterone Gels', 'Testosterone Pellet Therapy', 'Sublingual Testosterone'],
      icon: 'ri-shield-star-line',
      color: 'from-blue-500 to-cyan-500',
      image: 'https://public.readdy.ai/ai/img_res/5550f6f8c4dbd2ef8f5e38093fc3b04b.jpg'
    },
    {
      title: 'Estrogen Balance Therapy',
      description: 'Comprehensive estrogen replacement to alleviate menopause symptoms and support long-term health.',
      benefits: ['Relief from hot flashes and night sweats', 'Improved bone density', 'Enhanced skin elasticity', 'Better sleep quality'],
      methods: ['Estradiol Patches', 'Bioidentical Estrogen Creams', 'Estrogen Pellet Implants', 'Sublingual Estrogen Tablets'],
      icon: 'ri-heart-3-line',
      color: 'from-pink-500 to-rose-500',
      image: 'https://readdy.ai/api/search-image?query=Estrogen%20balance%20therapy%20for%20women%2C%20bioidentical%20hormone%20treatment%20setup%2C%20professional%20medical%20consultation%2C%20clean%20modern%20healthcare%20facility%2C%20hormone%20replacement%20therapy%20equipment%2C%20wellness%20and%20vitality%20theme&width=600&height=400&seq=estrogen-therapy&orientation=landscape'
    },
    {
      title: 'Thyroid Optimization',
      description: 'Advanced thyroid hormone replacement to optimize metabolism and energy production.',
      benefits: ['Improved metabolism and weight management', 'Enhanced energy levels', 'Better temperature regulation', 'Improved cognitive function'],
      methods: ['T4 (Levothyroxine) Therapy', 'T3 (Liothyronine) Treatment', 'Natural Desiccated Thyroid', 'Combination T4/T3 Protocols'],
      icon: 'ri-flashlight-line',
      color: 'from-yellow-500 to-orange-500',
      image: 'https://readdy.ai/api/search-image?query=Thyroid%20optimization%20therapy%2C%20medical%20consultation%20for%20thyroid%20treatment%2C%20professional%20healthcare%20setting%2C%20hormone%20therapy%20equipment%2C%20clean%20white%20medical%20environment%2C%20metabolism%20and%20energy%20enhancement%20theme&width=600&height=400&seq=thyroid-therapy&orientation=landscape'
    },
    {
      title: 'Growth Hormone Therapy',
      description: 'Cutting-edge growth hormone optimization for anti-aging and performance enhancement.',
      benefits: ['Enhanced muscle mass and strength', 'Improved skin elasticity', 'Better sleep quality', 'Increased bone density'],
      methods: ['Recombinant Growth Hormone Injections', 'Growth Hormone Releasing Peptides', 'Sermorelin Therapy', 'Ipamorelin Treatment'],
      icon: 'ri-rocket-line',
      color: 'from-purple-500 to-indigo-500',
      image: 'https://readdy.ai/api/search-image?query=Growth%20hormone%20therapy%20setup%2C%20anti-aging%20treatment%20equipment%2C%20professional%20medical%20facility%2C%20hormone%20optimization%20therapy%2C%20clean%20modern%20healthcare%20environment%2C%20performance%20enhancement%20medicine&width=600&height=400&seq=growth-hormone-therapy&orientation=landscape'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Comprehensive <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600">Treatment Options</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our personalized approach ensures you receive the most effective bioidentical hormone therapy 
            tailored to your unique physiological needs and health goals.
          </p>
        </div>

        <div className="space-y-16">
          {treatments.map((treatment, index) => (
            <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
              <div className="flex-1">
                <div className="flex items-center mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-r ${treatment.color} rounded-full flex items-center justify-center mr-4`}>
                    <i className={`${treatment.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">{treatment.title}</h3>
                </div>
                
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  {treatment.description}
                </p>
                
                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Key Benefits:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {treatment.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center">
                        <i className="ri-check-line text-green-600 mr-3"></i>
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Treatment Methods:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {treatment.methods.map((method, methodIndex) => (
                      <div key={methodIndex} className="flex items-center">
                        <i className="ri-medicine-bottle-line text-purple-600 mr-3"></i>
                        <span className="text-gray-700">{method}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex-1">
                <img 
                  src={treatment.image}
                  alt={treatment.title}
                  className="w-full h-96 object-cover object-top rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TreatmentOptions;